import { useMemo } from "react";
import {
  GetListResponse,
  useModal,
  useList,
  useCustomMutation,
  useGo,
  useInvalidate,
} from "@refinedev/core";
import { useLocation } from "react-router";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import {
  Typography,
  Box,
  Stack,
  useTheme,
  Button,
  Modal,
  Divider,
  Checkbox,
} from "@mui/material";
import { EditButton, ShowButton, DeleteButton } from "@refinedev/mui";
import React from "react";
import { IProduct } from "@/utils/zod/productCollectionFormValidation";
import {
  RowSelectionState,
  Updater,
  useReactTable,
  getCoreRowModel,
  ColumnDef,
} from "@tanstack/react-table";
import { IBroadcastChatListItem } from "@/utils/zod/broadcastChatSchema";
import { ChatList } from "@/pages/broadcast";
import { formatFileType } from "@/utils/formattingFunctions";
import CustomChip from "@/components/override/Chip";
import { API_URL } from "@/utils";
import { RestDataProvider } from "@/utils/data-provider/provider";
import { useTableState } from "@/hooks/useTableState";

type Props = {
  tableQuery: {
    data: GetListResponse<IProduct>,
    isLoading: boolean,
    isError: boolean,
    selectedRows: RowSelectionState,
    setSelectedRows: (updater: Updater<RowSelectionState>) => void,
    resetRowSelection: (defaultState: boolean) => void,
  };
};

export const ProductListTable: React.FC<Props> = ({ tableQuery }) => {
  const { data, isLoading, isError } = tableQuery;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const go = useGo();
  const { search } = useLocation();
  const invalidate = useInvalidate();
  const { tableState, onColumnFiltersChange, onGlobalFilterChange, onSortingChange } = useTableState<IProduct>();

  const columns = useMemo<MRT_ColumnDef<IProduct>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID #',
        size: 52,
        Cell: ({ row }) => <Typography>#{row.original.id}</Typography>,
      },
      {
        accessorKey: 'media',
        header: 'Image',
        enableOptions: false,
        size: 70,
        Cell: ({ row }) => (
          <Box
            component={formatFileType(row.original.medias?.[0]?.type)}
            src={row.original.medias?.[0]?.cdn_url.replace('image', 'image/compressed')}
            alt={row.original.name}
            sx={{ width: 48, height: 48, objectFit: 'contain' }}
            controls={false}
          />
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        filterVariant: 'text',
      },
      {
        accessorKey: 'sku_id',
        header: 'SKU ID',
      },
      {
        accessorKey: 'cost_price',
        header: 'Price',
      },
      {
        accessorKey: 'deal_name',
        header: 'Deal',
      },
      {
        accessorKey: 'minimum_order_quantity',
        header: 'MOQ',
        tooltip: 'Minimum Order Quantity',
      },
      {
        accessorKey: 'uom',
        header: 'UOM',
        tooltip: 'Unit of Measure',
      },
      {
        accessorKey: 'available_quantity',
        header: 'Available Qty',
        tooltip: 'Available Quantity',
        filterVariant: 'range',
      },
      {
        accessorKey: 'features',
        header: 'Features',
        Cell: ({ row }) => <Typography>{row.original.features?.join(", ")}</Typography>,
      },
      {
        accessorKey: 'material',
        header: 'Material',
      },
      {
        accessorKey: 'description',
        header: 'Description',
        Cell: ({ row }) => (
          <Typography
            variant="body2"
            sx={{
              maxWidth: 300,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {row.original.description}
          </Typography>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        filterVariant: 'select',
        Cell: ({ row }) => (
          <CustomChip label={row.original.status} status={row.original.status === "active" ? "positive" : "neutral"} type="status" size="small" />
        ),
      },
      {
        accessorKey: 'rating_stars',
        header: 'Rating',
      },
      {
        accessorKey: 'review_count',
        header: 'Reviews',
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <EditButton 
              hideText 
              resource="products" 
              recordItemId={row.id}
              meta={{
                to: `/products/${row.id}/edit`,
                query: Object.fromEntries(new URLSearchParams(search))
              }}
            />
            <ShowButton hideText recordItemId={row.id} />
            <DeleteButton hideText recordItemId={row.id} successNotification={
              (data: any) => {
                if (data.data.message.includes("Product had orders")) {
                  const { inserted_at, updated_at, ...product } = data.data?.product;
                  return {
                    message: data.data.message,
                    type: "progress",
                    undoableTimeout: 10,
                    async cancelMutation() {
                      await RestDataProvider(API_URL).update({
                        id: product.id,
                        resource: "products",
                        variables: {
                          ...product,
                          status: "active",
                        },
                      });

                      // Refetch the products list after cancellation
                      await invalidate({
                        resource: "products",
                        invalidates: ["list"],
                      });
                    },
                  }
                } else if(data.data.message.includes("Product deleted successfully")) {
                  return {
                    message: "Product deleted successfully",
                    description: "The product has been deleted successfully",
                    type: "success",
                  }
                } else {
                  return {
                    message: "Error deleting product",
                    description: "The product has not been deleted",
                    type: "error",
                  }
                }
              }
            }
            />
          </Stack>
        ),
      },
    ],
    [isDarkMode, search]
  );

  // Define columns for the chat list in the modal
  const chatColumns = useMemo<ColumnDef<IBroadcastChatListItem>[]>(
    () => [
      {
        id: 'select',
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
      },
      {
        accessorKey: 'id',
        header: 'ID',
        enableHiding: true,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: info => info.getValue(),
      },
    ],
    []
  );

  const { show, visible, close } = useModal();

  const { data: broadcasts } = useList<IBroadcastChatListItem>({
    resource: "rooms/broadcasts",
    queryOptions: {
      enabled: visible,
    }
  });

  const { mutateAsync: sendBroadcast } = useCustomMutation();

  // Create a TanStack table instance for the chat list
  const chatTable = useReactTable({
    data: broadcasts?.data ?? [],
    columns: chatColumns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    enableMultiRowSelection: true,
    getRowId: row => row.id,
  });

  const onSelectChat = (chatId: string) => {
    chatTable.setRowSelection({
      [chatId]: true,
    });
  };

  const handleSendBroadcast = async () => {
    // Get selected product IDs
    const selectedProductIds = Object.keys(tableQuery.selectedRows);
    const selectedChatIds = Object.keys(chatTable.getState().rowSelection);

    const selectedChats = broadcasts?.data.filter((chat) =>
      selectedChatIds.includes(chat.id)
    );

    const selectedProducts = data?.data.filter((product) =>
      selectedProductIds.includes(product.id.toString())
    );

    // Use only the first selected chat for navigation
    const firstSelectedChat = selectedChats?.[0];

    selectedChats?.forEach(async (chat) => {
      await sendBroadcast({
        url: "/rooms/broadcasts/messages",
        method: "post",
        values: {
          format: "product",
          origin: "broadcast::manual",
          room_id: chat.id,
          products: JSON.stringify(selectedProducts),
          type: "public",
          inserted_at: new Date().toISOString(),
          account_type: "seller",
          account_id: chat.broadcast_setting.sender_account_id,
        },
        successNotification: {
          message: "Broadcast sent successfully",
          description: "The broadcast has been sent successfully",
          type: "success",
        },
        errorNotification: {
          message: "Error sending broadcast",
          description: "The broadcast has not been sent",
          type: "error",
        },
      },
        {
          onSuccess() {
            if (firstSelectedChat && chat.id === firstSelectedChat.id) {
              // Navigate to the broadcast page with the selected chat ID
              go({
                to: "/rooms/broadcasts",
                type: "push",
                query: {
                  selectedChatId: chat.id
                }
              });
            }
          },
        });
    })

    tableQuery.resetRowSelection(true);
    chatTable.resetRowSelection();
    close();
  };

  // Check if any rows are selected
  const hasSelectedRows = Object.keys(tableQuery.selectedRows).length > 0;
  const hasSelectedChats = Object.keys(chatTable.getState().rowSelection).length > 0;

  const sortedData = useMemo(() => {
    return data?.data.sort((a, b) => {
      const dateA = a.updated_at ? new Date(a.updated_at) : new Date(0);
      const dateB = b.updated_at ? new Date(b.updated_at) : new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
  }, [data]);

  return (
    <>
      <MaterialReactTable
        columns={columns}
        data={sortedData ?? []}
        state={{
          ...tableState,
          isLoading,
          rowSelection: tableQuery.selectedRows,
          pagination: {
            pageIndex: 0,
            pageSize: 1000,
          },
        }}
        onColumnFiltersChange={onColumnFiltersChange}
        onGlobalFilterChange={onGlobalFilterChange}
        onSortingChange={onSortingChange}
        onRowSelectionChange={tableQuery.setSelectedRows}
        enableRowSelection
        enableMultiRowSelection
        enableColumnResizing
        enableColumnPinning
        enableFullScreenToggle
        enableDensityToggle
        enableGlobalFilterModes
        muiTableBodyRowProps={{ hover: true }}
        rowCount={data?.total}
        muiPaginationProps={{
          rowsPerPageOptions: [1000],
          showFirstButton: true,
          showLastButton: true,
        }}
        muiToolbarAlertBannerProps={
          isError
            ? {
              color: 'error',
              children: 'Error loading data',
            }
            : undefined
        }
        enableRowVirtualization
        // muiTableContainerProps={{ sx: { maxHeight: '500px' }}}
        muiBottomToolbarProps={{
          sx: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          },
        }}
        renderBottomToolbarCustomActions={() => (
          hasSelectedRows ? (
            <Button
              variant="contained"
              color="primary"
              onClick={show}
              sx={{ ml: 2 }}
            >
              Send in Broadcast ({Object.keys(tableQuery.selectedRows).length})
            </Button>
          ) : <div />
        )}
        positionGlobalFilter="right"
        getRowId={(row) => row.id?.toString() ?? ''}
      />
      <Modal open={visible} onClose={close}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxHeight: '90vh',
        }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Select Broadcast Channels
          </Typography>
          <Divider />
          <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: 'calc(90vh - 180px)' }}>
            <ChatList
              onChatSelect={onSelectChat}
              selectedChatIds={Object.keys(chatTable.getState().rowSelection)}
              allowCheckbox={true}
            />
          </Box>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendBroadcast}
              disabled={!hasSelectedChats}
            >
              Select
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default React.memo(ProductListTable);

